import { useState } from 'react'
import { StarRating } from './star-rating'
import { ReflectionCard } from './reflection-card'
import { ReflectionForm } from './reflection-form'
import { useReflections, useDeleteReflection } from '@/hooks/use-reflections'
import type { ActionPlanReflection } from '@/types'

interface ReflectionsSectionProps {
  planId: string
  planStatus: string
  isAdmin?: boolean
  currentUserId?: string
  archivedAt?: number | null
}

export function ReflectionsSection({ planId, planStatus, isAdmin, currentUserId, archivedAt }: ReflectionsSectionProps) {
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingReflection, setEditingReflection] = useState<ActionPlanReflection | undefined>()
  const { data: reflections, isLoading } = useReflections(planId)
  const deleteReflection = useDeleteReflection()

  // Only show for completed/expired/archived plans
  const canReflect = planStatus === 'completed' || planStatus === 'expired' || !!archivedAt
  if (!canReflect) return null

  const myReflection = reflections?.find((r) => r.user_id === currentUserId)
  const avgRating = reflections?.length
    ? Math.round((reflections.reduce((sum, r) => sum + r.effectiveness_rating, 0) / reflections.length) * 10) / 10
    : 0

  const handleEdit = (reflection: ActionPlanReflection) => {
    setEditingReflection(reflection)
    setIsFormOpen(true)
  }

  const handleDelete = async (reflection: ActionPlanReflection) => {
    if (window.confirm('Delete this reflection?')) {
      await deleteReflection.mutateAsync({ id: reflection.id, planId })
    }
  }

  const handleAdd = () => {
    setEditingReflection(myReflection)
    setIsFormOpen(true)
  }

  if (isLoading) return <div className="text-xs text-cfa-dark-gray/50 mt-2">Loading reflections...</div>

  return (
    <div className="mt-3 pt-3 border-t border-cfa-light-gray">
      {/* Summary bar */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-cfa-dark-gray/70">
            {reflections?.length || 0} reflection{reflections?.length !== 1 ? 's' : ''}
          </span>
          {avgRating > 0 && (
            <div className="flex items-center gap-1">
              <StarRating value={Math.round(avgRating)} readonly size="sm" />
              <span className="text-xs text-cfa-dark-gray/60">{avgRating}/5</span>
            </div>
          )}
        </div>
        <button
          onClick={handleAdd}
          className="text-xs text-brand-blue hover:text-brand-blue/80 font-medium"
        >
          {myReflection ? 'Edit My Reflection' : 'Add Reflection'}
        </button>
      </div>

      {/* Reflection cards */}
      {reflections && reflections.length > 0 && (
        <div className="space-y-2">
          {reflections.map((r) => (
            <ReflectionCard
              key={r.id}
              reflection={r}
              currentUserId={currentUserId}
              isAdmin={isAdmin}
              onEdit={() => handleEdit(r)}
              onDelete={() => handleDelete(r)}
            />
          ))}
        </div>
      )}

      {/* Form modal */}
      {isFormOpen && (
        <ReflectionForm
          key={editingReflection?.id ?? 'new'}
          isOpen={isFormOpen}
          onClose={() => { setIsFormOpen(false); setEditingReflection(undefined) }}
          planId={planId}
          existingReflection={editingReflection}
        />
      )}
    </div>
  )
}
